import type { ProviderAvailability, DayAvailability, TimeSlot, Booking, WeeklySchedule } from '@/types';

class CalendarService {
  private readonly API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://npm.rapidfix.ie';

  // Default weekly schedule template
  private defaultWeeklySchedule: WeeklySchedule = {
    monday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    tuesday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    wednesday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    thursday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    friday: { available: true, startTime: '09:00', endTime: '17:00', timeSlotDuration: 60, bufferTime: 15 },
    saturday: { available: false, startTime: '10:00', endTime: '16:00', timeSlotDuration: 60, bufferTime: 15 },
    sunday: { available: false, startTime: '10:00', endTime: '16:00', timeSlotDuration: 60, bufferTime: 15 }
  };

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  // Get provider availability settings
  async getProviderAvailability(providerId: string): Promise<ProviderAvailability> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }

      const result = await response.json();
      const data = result.data;

      // Fetch schedule
      const scheduleResponse = await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability/schedule`, {
        headers: this.getAuthHeaders()
      });

      let weeklySchedule = this.defaultWeeklySchedule;
      if (scheduleResponse.ok) {
        const scheduleResult = await scheduleResponse.json();
        const scheduleData = scheduleResult.data || [];
        
        // Convert backend schedule to frontend format
        weeklySchedule = this.convertScheduleToWeekly(scheduleData);
      }

      // Fetch blackout dates
      const blackoutResponse = await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability/blackout-dates`, {
        headers: this.getAuthHeaders()
      });

      let blackoutDates: any[] = [];
      if (blackoutResponse.ok) {
        const blackoutResult = await blackoutResponse.json();
        blackoutDates = blackoutResult.data || [];
      }

      // Fetch service areas
      const areasResponse = await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability/service-areas`, {
        headers: this.getAuthHeaders()
      });

      let serviceAreas: any[] = [];
      if (areasResponse.ok) {
        const areasResult = await areasResponse.json();
        serviceAreas = areasResult.data || [];
      }

      return {
        id: `availability-${providerId}`,
        providerId,
        weeklySchedule,
        blackoutDates: blackoutDates.map((bd: any) => ({
          startDate: bd.start_date || bd.startDate,
          endDate: bd.end_date || bd.endDate,
          reason: bd.reason
        })),
        bookingSettings: {
          instantBooking: data.instantBooking ?? true,
          advanceNoticeHours: data.advanceNoticeHours ?? 24,
          maxAdvanceBookingDays: data.maxAdvanceBookingDays ?? 90,
          allowWeekendBooking: data.allowWeekendBooking ?? false,
          allowHolidayBooking: data.allowHolidayBooking ?? false
        },
        serviceAreas: serviceAreas.map((area: any) => ({
          location: area.location,
          radius: area.radius_km || area.radius,
          travelTime: area.travel_time_minutes || area.travelTime
        })),
        createdAt: new Date(data.created_at || Date.now()),
        updatedAt: new Date(data.updated_at || Date.now())
      };
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      // Return default availability on error
      return {
        id: `availability-${providerId}`,
        providerId,
        weeklySchedule: this.defaultWeeklySchedule,
        blackoutDates: [],
        bookingSettings: {
          instantBooking: true,
          advanceNoticeHours: 24,
          maxAdvanceBookingDays: 90,
          allowWeekendBooking: false,
          allowHolidayBooking: false
        },
        serviceAreas: [],
        createdAt: new Date(),
        updatedAt: new Date()
      };
    }
  }

  private convertScheduleToWeekly(scheduleData: any[]): WeeklySchedule {
    const weekly: any = { ...this.defaultWeeklySchedule };
    
    scheduleData.forEach((day: any) => {
      const dayKey = day.day.toLowerCase() as keyof WeeklySchedule;
      weekly[dayKey] = {
        available: day.active,
        startTime: day.startTime,
        endTime: day.endTime,
        timeSlotDuration: day.timeSlotDuration || 60,
        bufferTime: day.bufferTime || 15
      };
    });

    return weekly;
  }

  // Update provider availability
  async updateProviderAvailability(providerId: string, availability: Partial<ProviderAvailability>): Promise<ProviderAvailability> {
    try {
      // Update basic availability settings
      if (availability.bookingSettings) {
        await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability`, {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            isAvailable: true,
            vacationMode: false,
            emergencyAvailable: false,
            instantBooking: availability.bookingSettings.instantBooking,
            advanceNoticeHours: availability.bookingSettings.advanceNoticeHours,
            maxAdvanceBookingDays: availability.bookingSettings.maxAdvanceBookingDays,
            allowWeekendBooking: availability.bookingSettings.allowWeekendBooking,
            allowHolidayBooking: availability.bookingSettings.allowHolidayBooking
          })
        });
      }

      // Update weekly schedule
      if (availability.weeklySchedule) {
        const workingDays = Object.entries(availability.weeklySchedule).map(([day, schedule]) => ({
          day: day.charAt(0).toUpperCase() + day.slice(1),
          active: schedule.available,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          timeSlotDuration: schedule.timeSlotDuration,
          bufferTime: schedule.bufferTime
        }));

        await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/availability/schedule`, {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ workingDays })
        });
      }

      // Return updated availability
      return await this.getProviderAvailability(providerId);
    } catch (error) {
      console.error('Failed to update availability:', error);
      throw error;
    }
  }

  // Generate time slots for a specific day
  generateTimeSlots(date: string, daySchedule: WeeklySchedule[keyof WeeklySchedule], existingBookings: Booking[] = []): TimeSlot[] {
    if (!daySchedule.available) return [];

    const slots: TimeSlot[] = [];
    const startTime = this.parseTime(daySchedule.startTime);
    const endTime = this.parseTime(daySchedule.endTime);
    const slotDuration = daySchedule.timeSlotDuration;
    const bufferTime = daySchedule.bufferTime;

    let currentTime = startTime;
    let slotId = 1;

    while (currentTime + slotDuration <= endTime) {
      const slotStart = this.formatTime(currentTime);
      const slotEnd = this.formatTime(currentTime + slotDuration);
      
      // Check if this slot conflicts with existing bookings
      const isBooked = existingBookings.some(booking => {
        const bookingStart = this.parseTime(booking.startTime);
        const bookingEnd = this.parseTime(booking.endTime);
        return (currentTime < bookingEnd && currentTime + slotDuration > bookingStart);
      });

      // Skip break time if defined
      const isBreakTime = daySchedule.breakStart && daySchedule.breakEnd &&
        currentTime < this.parseTime(daySchedule.breakEnd) &&
        currentTime + slotDuration > this.parseTime(daySchedule.breakStart);

      if (!isBreakTime) {
        slots.push({
          id: `${date}-slot-${slotId}`,
          startTime: slotStart,
          endTime: slotEnd,
          available: !isBooked,
          booked: isBooked,
          bookingId: isBooked ? existingBookings.find(b => 
            this.parseTime(b.startTime) <= currentTime && 
            this.parseTime(b.endTime) > currentTime
          )?.id : undefined
        });
      }

      currentTime += slotDuration + bufferTime;
      slotId++;
    }

    return slots;
  }

  // Get availability for a date range
  async getAvailabilityForRange(providerId: string, startDate: string, endDate: string): Promise<DayAvailability[]> {
    try {
      const availability = await this.getProviderAvailability(providerId);
      const bookings = await this.getProviderBookings(providerId, startDate, endDate);
      const days: DayAvailability[] = [];

      const start = new Date(startDate);
      const end = new Date(endDate);
      
      for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        const dateStr = date.toISOString().split('T')[0];
        const dayOfWeek = date.getDay();
        const dayName = this.getDayName(dayOfWeek);
        const daySchedule = availability.weeklySchedule[dayName];
        
        // Check if date is blacked out
        const isBlackout = availability.blackoutDates.some(blackout => 
          dateStr >= blackout.startDate && dateStr <= blackout.endDate
        );

        const dayBookings = bookings.filter(booking => 
          booking.scheduledDate.toISOString().split('T')[0] === dateStr
        );

        const timeSlots = isBlackout ? [] : this.generateTimeSlots(dateStr, daySchedule, dayBookings);

        days.push({
          date: dateStr,
          dayOfWeek,
          available: daySchedule.available && !isBlackout,
          timeSlots,
          isBlackout,
          blackoutReason: isBlackout ? 
            availability.blackoutDates.find(b => dateStr >= b.startDate && dateStr <= b.endDate)?.reason 
            : undefined
        });
      }

      return days;
    } catch (error) {
      console.error('Failed to get availability for range:', error);
      return [];
    }
  }

  // Book a time slot
  async bookTimeSlot(providerId: string, date: string, startTime: string, endTime: string, bookingData: Partial<Booking>): Promise<Booking> {
    try {
      // Calculate duration in minutes
      const duration = this.parseTime(endTime) - this.parseTime(startTime);

      // Prepare booking payload
      const payload: any = {
        customer_id: bookingData.customerId || '00000000-0000-0000-0000-000000000000', // Use null UUID for manual bookings
        customer_name: bookingData.customerName || '',
        provider_name: bookingData.providerName || 'Provider',
        service_type: bookingData.serviceType || '',
        scheduled_date: date,
        start_time: startTime,
        end_time: endTime,
        duration: duration,
        location: bookingData.location || '',
        notes: bookingData.notes || '',
        estimated_cost: bookingData.estimatedCost || 0
      };

      // job_id is optional - don't include it for manual bookings
      if (bookingData.jobId) {
        payload.job_id = bookingData.jobId;
      }

      // Send booking to backend
      const response = await fetch(`${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/bookings`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to create booking' }));
        throw new Error(errorData.error || 'Failed to create booking');
      }

      // Return the created booking
      const booking: Booking = {
        id: `booking-${Date.now()}`, // Backend should return the actual ID
        jobId: bookingData.jobId || '',
        customerId: bookingData.customerId || 'manual-booking',
        customerName: bookingData.customerName || '',
        providerId,
        providerName: bookingData.providerName || 'Provider',
        serviceType: bookingData.serviceType || '',
        scheduledDate: new Date(date),
        startTime,
        endTime,
        duration,
        location: bookingData.location || '',
        status: 'PENDING',
        notes: bookingData.notes,
        estimatedCost: bookingData.estimatedCost,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      return booking;
    } catch (error) {
      console.error('Failed to book time slot:', error);
      throw error;
    }
  }

  // Get bookings for a provider
  async getProviderBookings(providerId: string, startDate?: string, endDate?: string): Promise<Booking[]> {
    try {
      let url = `${this.API_BASE_URL}/api/v1/auth/providers/${providerId}/bookings`;
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const result = await response.json();
      const bookings = result.data || [];

      return bookings.map((booking: any) => {
        // Ensure scheduledDate is properly converted to Date object
        let scheduledDate: Date;
        if (booking.scheduled_date) {
          scheduledDate = new Date(booking.scheduled_date);
        } else if (booking.scheduledDate) {
          scheduledDate = booking.scheduledDate instanceof Date 
            ? booking.scheduledDate 
            : new Date(booking.scheduledDate);
        } else {
          scheduledDate = new Date();
        }

        return {
          id: booking.id,
          jobId: booking.job_id || booking.jobId,
          customerId: booking.customer_id || booking.customerId,
          customerName: booking.customer_name || booking.customerName,
          providerId: booking.provider_id || booking.providerId,
          providerName: booking.provider_name || booking.providerName,
          serviceType: booking.service_type || booking.serviceType,
          scheduledDate: scheduledDate,
          startTime: booking.start_time || booking.startTime,
          endTime: booking.end_time || booking.endTime,
          duration: booking.duration,
          location: booking.location,
          status: booking.status,
          notes: booking.notes,
          estimatedCost: booking.estimated_cost || booking.estimatedCost,
          actualCost: booking.actual_cost || booking.actualCost,
          cancellationReason: booking.cancellation_reason || booking.cancellationReason,
          rescheduledFrom: booking.rescheduled_from ? new Date(booking.rescheduled_from) : undefined,
          createdAt: new Date(booking.created_at || booking.createdAt || Date.now()),
          updatedAt: new Date(booking.updated_at || booking.updatedAt || Date.now())
        };
      });
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      // Return mock data on error for development
      return this.getMockBookings(providerId, startDate, endDate);
    }
  }

  // Cancel a booking
  cancelBooking(bookingId: string, reason: string): Promise<Booking> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock cancellation
        const booking: Booking = {
          id: bookingId,
          jobId: 'job-123',
          customerId: 'customer-123',
          customerName: 'John Doe',
          providerId: 'provider-123',
          providerName: 'Elite Services',
          serviceType: 'Plumbing',
          scheduledDate: new Date(),
          startTime: '10:00',
          endTime: '12:00',
          duration: 120,
          location: 'Dublin',
          status: 'CANCELLED',
          cancellationReason: reason,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        resolve(booking);
      }, 500);
    });
  }

  // Reschedule a booking
  rescheduleBooking(bookingId: string, newDate: string, newStartTime: string, newEndTime: string): Promise<Booking> {
    return new Promise((resolve) => {
      setTimeout(() => {
        // Mock reschedule
        const booking: Booking = {
          id: bookingId,
          jobId: 'job-123',
          customerId: 'customer-123',
          customerName: 'John Doe',
          providerId: 'provider-123',
          providerName: 'Elite Services',
          serviceType: 'Plumbing',
          scheduledDate: new Date(newDate),
          startTime: newStartTime,
          endTime: newEndTime,
          duration: this.parseTime(newEndTime) - this.parseTime(newStartTime),
          location: 'Dublin',
          status: 'RESCHEDULED',
          rescheduledFrom: new Date(),
          createdAt: new Date(),
          updatedAt: new Date()
        };
        resolve(booking);
      }, 500);
    });
  }

  // Helper methods
  private parseTime(timeStr: string): number {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  private getDayName(dayOfWeek: number): string {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days[dayOfWeek];
  }

  private getMockBookings(providerId: string, startDate?: string, endDate?: string): Booking[] {
    // Mock bookings data
    const mockBookings: Booking[] = [
      {
        id: 'booking-1',
        jobId: 'job-1',
        customerId: 'customer-1',
        customerName: 'John Smith',
        providerId,
        providerName: 'Elite Services',
        serviceType: 'Plumbing Repair',
        scheduledDate: new Date('2024-12-20'),
        startTime: '10:00',
        endTime: '12:00',
        duration: 120,
        location: 'Dublin City Center',
        status: 'CONFIRMED',
        estimatedCost: 150,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: 'booking-2',
        jobId: 'job-2',
        customerId: 'customer-2',
        customerName: 'Sarah Johnson',
        providerId,
        providerName: 'Elite Services',
        serviceType: 'Electrical Work',
        scheduledDate: new Date('2024-12-21'),
        startTime: '14:00',
        endTime: '16:00',
        duration: 120,
        location: 'Dublin Suburbs',
        status: 'CONFIRMED',
        estimatedCost: 200,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];

    // Filter by date range if provided
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return mockBookings.filter(booking => 
        booking.scheduledDate >= start && booking.scheduledDate <= end
      );
    }

    return mockBookings;
  }
}

export const calendarService = new CalendarService();